// Resolve um CEP digitado no formulário pros IDs que a API de Protocolo espera
// nos campos lookup de endereço (CEP/Estado/Município em DadosRequerimento) —
// reaproveita a API de Endereços (addressService) já integrada no projeto.
// Confirmado com o usuário: os IDs que essa API devolve (endereco.id, estadoId,
// cidadeId) são os mesmos das tabelas de referência que a API de Protocolo espera.
//
// Best-effort: CEP inválido, não encontrado, ou fora do ar retornam null em vez
// de lançar erro — quem chama (dadosRequerimentoService) simplesmente não manda
// esses 3 campos naquela gravação, sem travar o resto do requerimento.
import { addressService } from '../../../address/services/addressService';
import { escolherRegistroCep } from '../../../address/utils/cepRegistros';
import { unformat } from '../../../../utils/formatters';
import logger from '../../../../utils/logger';

// CEP com vários registros (CEP único de localidade): `criterios.cepId` é o registro
// que a pessoa escolheu na tela (campo `{campoCep}Id` do formData, gravado pelo campo
// `cep` em fieldComponents.js; ver criteriosCepDoFormulario em cepRegistros.js). Sem
// ele, tenta casar por logradouro/bairro; se ainda ficar ambíguo, devolve null (ver
// escolherRegistroCep) em vez de chutar um registro.
export async function resolverEnderecoPorCep(cep, criterios = {}) {
  const cepNumeros = unformat(cep || '');
  if (cepNumeros.length !== 8) {
    return null;
  }

  try {
    const response = await addressService.getByCEP(cepNumeros);
    const endereco = escolherRegistroCep(response.data?.data || [], criterios);

    if (!endereco) {
      return null;
    }

    return {
      cepId: endereco.id ?? null,
      estadoId: endereco.estadoId ?? null,
      municipioId: endereco.cidadeId ?? null,
      paisId: endereco.paisId ?? null
    };
  } catch (error) {
    logger.warn(`Não foi possível resolver o CEP "${cep}" pra DadosRequerimento:`, error.message);
    return null;
  }
}

// Busca o endereço completo por CEP pra autopreencher a tela (Logradouro/Bairro/
// Município/Estado/País) — usado pelo campo `cep` (CepField) em fieldComponents.js,
// mesmo endpoint de resolverEnderecoPorCep acima, mas devolvendo os registros
// completos, não só os IDs. Devolve um `status` (encontrado / nao_encontrado / erro /
// invalido) pra quem chama distinguir "não encontrado" (404) de erro técnico e
// mostrar a mensagem certa pra pessoa. `registros` vem sempre como lista: com mais
// de um, a pessoa escolhe o dela na tela.
export async function buscarEnderecoCompleto(cep) {
  const cepNumeros = unformat(cep || '');
  if (cepNumeros.length !== 8) {
    return { status: 'invalido' };
  }

  try {
    const response = await addressService.getByCEP(cepNumeros);
    const registros = response.data?.data || [];

    if (!registros.length) {
      return { status: 'nao_encontrado' };
    }

    return { status: 'encontrado', registros };
  } catch (error) {
    if (error.response?.status === 404) {
      return { status: 'nao_encontrado' };
    }
    logger.warn(`Erro ao buscar o CEP "${cep}":`, error.message);
    return { status: 'erro' };
  }
}

// Busca o único município cujo nome bate exatamente (case-insensitive) com o texto
// informado. Ambíguo (ex: "CAMPO GRANDE" e "CAMPO GRANDE DO SUL") ou não encontrado
// não arrisca escolher um dos dois — devolve null, mesmo princípio de "nunca
// inventar" usado nos demais campos deste módulo. Compartilhado por
// resolverMunicipioPorNome (só o ID) e resolverMunicipioComLabelPorNome (ID + rótulo
// de exibição, usado no pré-preenchimento do autocomplete).
async function buscarMunicipioExatoPorNome(nome) {
  const nomeLimpo = String(nome || '').trim();
  if (nomeLimpo.length < 3) {
    return null;
  }

  try {
    const response = await addressService.searchCities(nomeLimpo);
    const resultados = response?.data || [];
    const exatos = resultados.filter((m) => String(m.nome || '').trim().toLowerCase() === nomeLimpo.toLowerCase());

    return exatos.length === 1 ? exatos[0] : null;
  } catch (error) {
    logger.warn(`Não foi possível resolver o município "${nome}":`, error.message);
    return null;
  }
}

// Resolve o ID de um município a partir do nome digitado (campos "Naturalidade" e
// "Município do Título de Eleitor" nos pedidos que ainda são texto livre) — usado na
// hora de montar o DadosRequerimento.
export async function resolverMunicipioPorNome(nome) {
  const municipio = await buscarMunicipioExatoPorNome(nome);
  return municipio ? municipio.id : null;
}

// Resolve ID + rótulo de exibição (ex: "CAMPO GRANDE - MS") a partir do nome — usado
// pra pré-preencher o campo `municipioBusca` (autocomplete) com o que já vem do
// cadastro/perfil, que devolve só o nome em texto (perfilPrefillService.js).
export async function resolverMunicipioComLabelPorNome(nome) {
  const municipio = await buscarMunicipioExatoPorNome(nome);
  return municipio ? { id: municipio.id, label: `${municipio.nome} - ${municipio.estadoSigla}` } : null;
}

